import { RenderItemProps, VirtualList } from '@shared/components/ui/VirtualList';
import { Location } from '../../models/Location';
import { LocationCard } from './LocationCard';

// 🎯 Interface Segregation Principle (ISP) - Mismas props que LocationTable
interface LocationCardListProps {
  locations: Location[];
  // 🎯 Acciones del dropdown (compartidas con tabla)
  onLocationEdit?: (location: Location) => void;
  onLocationDelete?: (location: Location) => void;
  onLocationAdjust?: (location: Location) => void;
  onLocationPrint?: (location: Location, size: 'small' | 'large') => void;
  onLocationToggleLock?: (location: Location) => void;
  isLoading?: boolean;
  className?: string;
  // 🎯 Props para scroll infinito
  fetchNextPage?: () => void;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  // 🎯 Props para sorting - manejado desde el padre
  sorting?: any;
  onSortingChange?: (sorting: any) => void;
  total?: number;
  // 🎯 Props específicas para cards
  showActions?: boolean;
  // 🎯 Prop para estimación de altura cuando es dinámica
  estimateItemHeight?: number;
}

export const LocationCardList = ({
  locations,
  onLocationEdit,
  onLocationDelete,
  onLocationAdjust,
  onLocationPrint,
  onLocationToggleLock,
  fetchNextPage,
  hasNextPage = false,
  isFetchingNextPage = false,
  sorting,
  onSortingChange,
  total,
  className = '',
  showActions = true,
  estimateItemHeight = 120, // 🎯 Estimación conservadora para cards con productos
}: LocationCardListProps) => {
  // 🎯 Render function que usa el componente LocationCard
  const renderLocationCard = (props: RenderItemProps<Location>) => (
    <LocationCard
      {...props}
      onEdit={onLocationEdit}
      onDelete={onLocationDelete}
      onAdjust={onLocationAdjust}
      onPrint={onLocationPrint}
      onToggleLock={onLocationToggleLock}
      showActions={showActions}
    />
  );

  // 🎯 Key generator para optimizar renders
  const getLocationKey = (location: Location) => {
    return location.id;
  };

  return (
    <VirtualList
      data={locations}
      renderItem={renderLocationCard}
      itemKey={getLocationKey}
      // 🎯 Configuración optimizada para móvil con altura dinámica
      estimateItemHeight={estimateItemHeight} // 🎯 Solo estimación, altura real será medida
      height="calc(100vh - 150px)" // Altura completa en móvil
      gap={0} // Sin gap entre cards
      className={`rounded-lg shadow-sm ${className}`}
      // 🎯 Props de scroll infinito
      fetchNextPage={fetchNextPage}
      hasNextPage={hasNextPage}
      isFetchingNextPage={isFetchingNextPage}
      infiniteScrollThreshold={300} // Threshold mayor para móvil
      // 🎯 Props de sorting (aunque en móvil se use menos)
      sorting={sorting}
      onSortingChange={onSortingChange}
      total={total}
      // 🎯 Styling específico para móvil
      listClassName="touch-pan-y" // Mejor scroll en mobile
      itemClassName="touch-manipulation" // Optimización touch
    />
  );
};
